import React, { createContext, useContext, useMemo, type RefObject } from 'react';
import type { ModelId, ViewId } from 'balus-common';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import type { ModelCollection } from '@view-model/domain/model/ModelCollection';
import type { ViewModelPermissions } from '@view-model/application/context';
import { type ViewModelPageOperation } from '@view-model/application/operation';
import type { Rect } from '@view-model/models/common/basic/Rect';

type AIAssistantOperationContext = {
  operations: Record<ModelId, StickyModelContentsOperation>;
  models: ModelCollection;
  selectedViewIds: Set<ViewId>;
  viewModelPermissions: ViewModelPermissions;
  viewModelPageOperationRef: RefObject<ViewModelPageOperation | undefined | null>;
  // 指定した矩形が画面に収まるようにキャンバスを移動・ズームする
  focusRect: (rect: Rect) => void;
};

const AIAssistantOperationContext = createContext<AIAssistantOperationContext | null>(null);

export const AIAssistantOperationProvider: React.FC<React.PropsWithChildren<AIAssistantOperationContext>> = ({
  operations,
  models,
  selectedViewIds,
  viewModelPermissions,
  children,
  viewModelPageOperationRef,
  focusRect,
}) => {
  // ビューのドラッグ中は親が毎フレーム再レンダーされる。value をメモ化しないと
  // 中身が同一でも参照が変わり、AI チャット側が丸ごと再レンダーされてしまう。
  const value = useMemo(
    () => ({ viewModelPageOperationRef, operations, models, selectedViewIds, viewModelPermissions, focusRect }),
    [viewModelPageOperationRef, operations, models, selectedViewIds, viewModelPermissions, focusRect]
  );

  return <AIAssistantOperationContext.Provider value={value}>{children}</AIAssistantOperationContext.Provider>;
};

export function useAIAssistantOperationContext(): AIAssistantOperationContext {
  const context = useContext(AIAssistantOperationContext);
  if (!context) {
    throw new Error('AIAssistantOperationContext is not provided');
  }

  return context;
}
